import { Plugin } from "prosemirror-state";
import type { EditorState, Transaction } from "prosemirror-state";
import type { Node as PmNode } from "prosemirror-model";
import { schema } from "./schema";

// A clipboard payload that is exactly one URL and nothing else. Anchored (and
// http(s)-only, matching the auto-detect plugin's deliberate conservatism) so
// pasting prose that merely contains a URL still replaces the selection.
const BARE_URL = /^https?:\/\/[^\s<>]+$/;

// A run of selected text that may carry a link mark.
type Range = { from: number; to: number; node: PmNode; pos: number };

// Collect the parts of the selection that can be linked: text only, in a parent
// that allows the mark (excludes code blocks, whose `marks: ""` forbids it), and
// not already code-marked (same carve-out the auto-detect plugin makes).
// Inline atoms are skipped too — a `card_link` is inline, so an unguarded
// addMark would nest an external href around an internal card link.
function linkableRanges(state: EditorState): Range[] {
  const link = schema.marks["link"]!;
  const code = schema.marks["code"]!;
  const { from, to } = state.selection;
  const ranges: Range[] = [];

  state.doc.nodesBetween(from, to, (node, pos, parent) => {
    if (!node.isText || !node.text) return;
    if (!parent || !parent.type.allowsMarkType(link)) return;
    if (code.isInSet(node.marks)) return;
    const start = Math.max(pos, from);
    const end = Math.min(pos + node.nodeSize, to);
    if (end > start) ranges.push({ from: start, to: end, node, pos });
  });
  return ranges;
}

// Shrink the selection off surrounding whitespace, so double-clicking a word
// (browsers usually include the trailing space) links the word, not the space.
// Only the outer edges matter — interior whitespace is part of the phrase.
function trimEdges(ranges: Range[]): Range[] {
  const first = ranges[0]!;
  const firstText = first.node.text!;
  while (first.from < first.to && /\s/.test(firstText[first.from - first.pos]!)) {
    first.from++;
  }
  const last = ranges[ranges.length - 1]!;
  const lastText = last.node.text!;
  while (last.to > last.from && /\s/.test(lastText[last.to - last.pos - 1]!)) {
    last.to--;
  }
  return ranges.filter((r) => r.to > r.from);
}

/** Mark the current selection as a link to `url`, or null if none of it can
 * hold one (all code, atoms, or whitespace). The selection is left alone. */
function linkSelectionTr(state: EditorState, url: string): Transaction | null {
  const ranges = linkableRanges(state);
  if (ranges.length === 0) return null;
  const trimmed = trimEdges(ranges);
  if (trimmed.length === 0) return null;

  const mark = schema.marks["link"]!.create({ href: url });
  const tr = state.tr;
  for (const r of trimmed) tr.addMark(r.from, r.to, mark);
  return tr;
}

// Pasting a URL over selected text turns that text into a hyperlink instead of
// replacing it — the behaviour every other editor has. Anything else (empty
// selection, non-URL clipboard, nothing linkable in range) falls through to the
// normal paste.
export function linkPastePlugin(): Plugin {
  return new Plugin({
    props: {
      handlePaste(view, event) {
        if (view.state.selection.empty) return false;
        const text = event.clipboardData?.getData("text/plain").trim();
        if (!text || !BARE_URL.test(text)) return false;

        const tr = linkSelectionTr(view.state, text);
        if (!tr) return false;
        view.dispatch(tr);
        return true;
      },
    },
  });
}
